import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import './PDFViewerCanvas.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Parse PDF.js outline into section structure with page numbers
 */
async function parseOutline(outline, pdf) {
  const sections = [];
  
  async function processOutlineItem(item, level = 0) {
    try {
      let pageNum = null;
      
      if (item.dest) {
        let dest = item.dest;
        if (typeof dest === 'string') {
          dest = await pdf.getDestination(dest);
        }
        if (Array.isArray(dest) && dest.length > 0) {
          const pageRef = dest[0];
          const pageIndex = await pdf.getPageIndex(pageRef);
          pageNum = pageIndex + 1;
        }
      }
      
      const section = {
        title: item.title,
        level: level,
        page: pageNum,
        children: []
      };
      
      if (item.items && item.items.length > 0) {
        for (const child of item.items) {
          const childSection = await processOutlineItem(child, level + 1);
          section.children.push(childSection);
        }
      }
      
      return section;
    } catch (err) {
      console.warn('Error processing outline item:', item.title, err);
      return {
        title: item.title,
        level: level,
        page: null,
        children: []
      };
    }
  }
  
  for (const item of outline) {
    const section = await processOutlineItem(item, 0);
    sections.push(section);
  }
  
  return sections;
}

/**
 * Detect paragraph boundaries from PDF text content
 * Uses line breaks and vertical spacing to identify paragraphs.
 * pageWidth (PDF points) makes the column thresholds adaptive — a fixed 150pt
 * leftward-jump threshold is wrong on non-letter page sizes (too large on narrow
 * pages → real column jumps missed; too small on wide pages → false splits).
 */
function detectParagraphs(textContent, pageWidth = 612) {
  const items = textContent.items;
  if (!items || items.length === 0) return [];

  const paragraphs = [];
  let currentParagraph = {
    text: '',
    items: [],
    startY: null,
    endY: null,
    spanIndices: [],
    maxXEnd: null, // rightmost text edge seen (x + width) in current paragraph
  };

  let lastY = null;
  let lastX = null;
  let spanIndex = 0;
  const lineHeightThreshold = 15;
  // Leftward x-jump threshold: right-col → left-col transition (PDF pts).
  // ~25% of page width — on a 612pt letter page that's ~153pt (≈ the old hard
  // -coded 150), but it now scales correctly for narrower/wider pages.
  const leftwardJumpThreshold = pageWidth * 0.25;
  // Rightward gap threshold: gap between end of current paragraph's text and
  // start of a new item on the same line — if larger than this, it's a column
  // boundary (left-col → right-col). Typical gutter is 18-36 pts; word spacing
  // is 2-5 pts. 15 pts sits safely between them.
  const rightwardGapThreshold = 15;

  items.forEach((item) => {
    const transform = item.transform;
    const x = transform[4];
    const y = transform[5];
    const itemWidth = item.width || 0;
    const xEnd = x + itemWidth;
    const text = item.str;

    if (!text.trim()) {
      spanIndex++; // keep index aligned with PDF.js rendered spans (which include empty items)
      return;
    }

    if (lastY === null) {
      currentParagraph.text = text;
      currentParagraph.items.push(item);
      currentParagraph.startY = y;
      currentParagraph.endY = y;
      currentParagraph.spanIndices.push(spanIndex);
      currentParagraph.maxXEnd = xEnd;
      lastY = y;
      lastX = x;
      spanIndex++;
      return;
    }

    const verticalGap = Math.abs(lastY - y);
    const onSameLine = verticalGap <= lineHeightThreshold;

    // Right-col → left-col: large leftward jump on the same line
    const leftwardColumnJump = onSameLine && (lastX - x) > leftwardJumpThreshold;

    // Left-col → right-col: item starts well past the rightmost edge we've seen
    // Only fire when we have a reliable item width to track the edge.
    const rightwardColumnJump = onSameLine &&
      itemWidth > 0 &&
      currentParagraph.maxXEnd !== null &&
      (x - currentParagraph.maxXEnd) > rightwardGapThreshold;

    if (verticalGap > lineHeightThreshold || leftwardColumnJump || rightwardColumnJump) {
      if (currentParagraph.text.trim()) {
        paragraphs.push({
          text: currentParagraph.text.trim(),
          items: [...currentParagraph.items],
          startY: currentParagraph.startY,
          endY: currentParagraph.endY,
          spanIndices: [...currentParagraph.spanIndices]
        });
      }

      currentParagraph = {
        text: text,
        items: [item],
        startY: y,
        endY: y,
        spanIndices: [spanIndex],
        maxXEnd: xEnd,
      };
    } else {
      const needsSpace = currentParagraph.text.length > 0 &&
                        !currentParagraph.text.endsWith(' ') &&
                        !text.startsWith(' ');

      currentParagraph.text += (needsSpace ? ' ' : '') + text;
      currentParagraph.items.push(item);
      currentParagraph.endY = y;
      currentParagraph.spanIndices.push(spanIndex);
      currentParagraph.maxXEnd = Math.max(currentParagraph.maxXEnd ?? 0, xEnd);
    }

    lastY = y;
    lastX = x;
    spanIndex++;
  });

  if (currentParagraph.text.trim()) {
    paragraphs.push({
      text: currentParagraph.text.trim(),
      items: [...currentParagraph.items],
      startY: currentParagraph.startY,
      endY: currentParagraph.endY,
      spanIndices: [...currentParagraph.spanIndices]
    });
  }

  return paragraphs;
}

/**
 * Find which paragraph contains the selected text
 */
function findParagraphContainingSelection(selectedText, paragraphs) {
  if (!selectedText || !paragraphs.length) return null;
  
  const normalizedSelection = selectedText.trim().toLowerCase();
  
  for (const paragraph of paragraphs) {
    const normalizedParagraph = paragraph.text.toLowerCase();
    
    if (normalizedParagraph.includes(normalizedSelection)) {
      return paragraph;
    }
  }
  
  return null;
}

/**
 * Restrict spans + highlight bounds to a single column.
 *
 * Returns { spans, clampLeft, clampRight }. clampLeft/clampRight are absolute
 * viewport x-coords that the rendered highlight rectangles must not cross.
 * ±Infinity means no clamp on that side.
 *
 * Strategy: always sample substantial spans (width ≥ 20 px) across the whole
 * textLayerDiv in the paragraph's y-band to derive the FOUR column edges
 * (left-col left/right, right-col left/right). Then:
 *   • If the paragraph straddles two columns (largest inter-span gap big
 *     enough to be a gutter), pick the cluster whose joined text contains
 *     the anchor; fall back to the larger cluster.
 *   • Otherwise pick the column by the paragraph's own x-center vs page mid.
 * The chosen side's clamps span its full visual width — so each rendered
 * highlight row extends to the column's true edge, not just to the rightmost
 * paragraph span (which often stops a few px shy of the visual edge).
 */
function filterSpansToSameColumn(spans, anchorText, textLayerDiv) {
  const noClamp = { clampLeft: -Infinity, clampRight: Infinity };
  if (!spans || spans.length === 0) return { spans, ...noClamp };

  const items = spans
    .map(span => ({ span, rect: span.getBoundingClientRect() }))
    .filter(it => it.rect.width > 0 && it.rect.height > 0);
  if (items.length === 0 || !textLayerDiv) return { spans, ...noClamp };

  // ── Column geometry: sample the whole text layer (not just a narrow y-band).
  // A 20 px y-band was too narrow — if the adjacent column has a figure or
  // heading at the paragraph's y position, the opposite-side half would come
  // up empty and we'd skip column detection entirely.
  const containerRect = textLayerDiv.getBoundingClientRect();
  const pageMid = containerRect.left + containerRect.width / 2;
  const SUBSTANTIAL_WIDTH = 8; // wide enough to skip lone punctuation marks
  const allRects = Array.from(textLayerDiv.querySelectorAll('span[role="presentation"]'))
    .map(s => s.getBoundingClientRect())
    .filter(r => r.width >= SUBSTANTIAL_WIDTH);
  const leftHalf  = allRects.filter(r => r.right <= pageMid);
  const rightHalf = allRects.filter(r => r.left  >= pageMid);

  // Require a genuine gutter between the two halves. Single-column sections
  // (centered abstract, full-width heading) have text meeting at the midpoint
  // with gutter ≈ 0; real two-column body text has a visible gap.
  if (leftHalf.length === 0 || rightHalf.length === 0) {
    console.log(`filterSpansToSameColumn: single-column page (leftHalf=${leftHalf.length}, rightHalf=${rightHalf.length}), no clamp`);
    return { spans, ...noClamp };
  }
  const lcMinLeft  = Math.min(...leftHalf.map(r => r.left));
  const lcMaxRight = Math.max(...leftHalf.map(r => r.right));
  const rcMinLeft  = Math.min(...rightHalf.map(r => r.left));
  const rcMaxRight = Math.max(...rightHalf.map(r => r.right));
  const gutter = rcMinLeft - lcMaxRight;
  console.log(`filterSpansToSameColumn: pageMid=${pageMid.toFixed(0)} gutter=${gutter.toFixed(1)} (${(gutter/containerRect.width*100).toFixed(1)}% of page) lcMaxRight=${lcMaxRight.toFixed(0)} rcMinLeft=${rcMinLeft.toFixed(0)} rcMaxRight=${rcMaxRight.toFixed(0)}`);
  // Two-column gutters in academic papers are ~12-40px (at typical render scale);
  // word spacing is well under ~7px. 3% of page width (~24px at full width) was
  // too strict and missed thin-gutter papers, treating them as single-column. 1.8%
  // (~14px) catches thin gutters while staying clear of word-spacing noise. (The
  // ratio is scale-invariant — gutter and width both scale with zoom together.)
  if (gutter < containerRect.width * 0.018) {
    console.log(`filterSpansToSameColumn: gutter too small, no clamp`);
    return { spans, ...noClamp }; // no real column gutter on this page
  }

  // ── Detect whether THIS paragraph straddles both columns.
  const sorted = [...items].sort((a, b) => a.rect.left - b.rect.left);
  const xMin  = sorted[0].rect.left;
  const xMax  = sorted[sorted.length - 1].rect.right;
  const xRange = xMax - xMin;

  let maxGap = 0;
  let splitAfterIdx = -1;
  for (let i = 0; i < sorted.length - 1; i++) {
    const gap = sorted[i + 1].rect.left - sorted[i].rect.right;
    if (gap > maxGap) { maxGap = gap; splitAfterIdx = i; }
  }
  const straddles =
    splitAfterIdx >= 0 &&
    xRange > 40 &&
    maxGap >= gutter * 0.5 && // gap must be at least half the detected gutter
    maxGap / xRange >= 0.10;

  let chosenSide;
  let allowedSpans = spans;

  if (straddles) {
    const leftCluster  = sorted.slice(0, splitAfterIdx + 1);
    const rightCluster = sorted.slice(splitAfterIdx + 1);

    // Anchor text matching with NFKC normalisation (handles ligatures like ﬁ→fi)
    const norm = (s) => (s || '').normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
    let chosen = null;
    if (anchorText) {
      const needle = norm(anchorText).slice(0, 35);
      if (needle.length >= 3) {
        for (const cluster of [leftCluster, rightCluster]) {
          if (norm(cluster.map(it => it.span.textContent).join(' ')).includes(needle)) {
            chosen = cluster; break;
          }
        }
      }
    }
    // Fallback: which cluster is on the correct side of the page gutter?
    // (avoids the old "pick bigger cluster" bias that always chose left column)
    if (!chosen) {
      const lcCenterX = leftCluster.reduce((s, it) => s + (it.rect.left + it.rect.right) / 2, 0) / leftCluster.length;
      const rcCenterX = rightCluster.reduce((s, it) => s + (it.rect.left + it.rect.right) / 2, 0) / rightCluster.length;
      // anchorText position is unknown; prefer left cluster only if it's
      // unambiguously left-of-gutter.
      chosen = lcCenterX < lcMaxRight ? leftCluster : rightCluster;
      // If still ambiguous, default to whichever side the paragraph center falls on.
      const pCenter = (xMin + xMax) / 2;
      if (lcCenterX >= lcMaxRight) chosen = pCenter < pageMid ? leftCluster : rightCluster;
    }
    chosenSide = chosen === leftCluster ? 'left' : 'right';
    const allowed = new Set(chosen.map(it => it.span));
    allowedSpans = spans.filter(s => allowed.has(s));
  } else {
    // Single-column paragraph — which column is it in?
    // Compare the paragraph's leftmost span against pageMid (the viewport
    // midpoint of the text layer). This is more stable than rcMinLeft, which
    // is computed only from spans whose left edge is ≥ pageMid and can sit a
    // few px to the RIGHT of the actual right-column start — causing
    // right-column paragraphs whose leftmost span falls just left of that
    // estimate to be misclassified as left-column.
    chosenSide = xMin < pageMid ? 'left' : 'right';
    console.log(`filterSpansToSameColumn: single-col para xMin=${xMin.toFixed(0)} vs pageMid=${pageMid.toFixed(0)} → chosenSide=${chosenSide}`);
  }

  // Push each column's highlight to its full visual extent.
  let clampLeft  = -Infinity;
  let clampRight = Infinity;
  if (chosenSide === 'left') {
    clampLeft  = lcMinLeft;
    clampRight = rcMinLeft;
  } else {
    clampLeft  = lcMaxRight;
    clampRight = rcMaxRight;
  }

  return { spans: allowedSpans, clampLeft, clampRight };
}

/**
 * Highlight a paragraph in the PDF
 */
function highlightParagraph(paragraph, textLayerDiv, viewport, anchorText = null) {
  if (!paragraph || !textLayerDiv) return [];

  const allSpans = Array.from(textLayerDiv.querySelectorAll('span[role="presentation"]'));

  if (allSpans.length === 0) {
    console.warn('No text spans found in text layer');
    return [];
  }

  let paragraphSpans = paragraph.spanIndices
    .map(idx => allSpans[idx])
    .filter(span => span);

  if (paragraphSpans.length === 0) {
    console.warn('No spans found for paragraph indices:', paragraph.spanIndices);
    return [];
  }

  // Restrict highlight to a single column. Uses the anchor text if available
  // to pick the right column when the paragraph straddles two. Also returns
  // a clamp range so we can clip wide PDF.js spans at the column gutter.
  // textLayerDiv is passed so we can sample SUBSTANTIAL spans across the
  // page to find the true column boundary (paragraph spans alone aren't
  // enough — they may not include any wide spans in the other column).
  const filterResult = filterSpansToSameColumn(paragraphSpans, anchorText, textLayerDiv);
  paragraphSpans = filterResult.spans;
  const clampLeft  = filterResult.clampLeft;
  const clampRight = filterResult.clampRight;

  const containerRect = textLayerDiv.getBoundingClientRect();

  // Merge spans onto the same visual line into one bounding box per line,
  // but keep spans in different columns separate (large horizontal gap = new column).
  const lineGroups = [];
  paragraphSpans.forEach(span => {
    const rect = span.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    // Clamp each span's contribution to the chosen column's gutter boundary,
    // so an over-wide PDF.js span (whose DOM rect extends past the column)
    // can't drag the line group's right/left edge into the other column.
    const clampedLeft  = Math.max(rect.left,  clampLeft);
    const clampedRight = Math.min(rect.right, clampRight);
    if (clampedRight <= clampedLeft) return; // span lies fully outside our column
    const match = lineGroups.find(g =>
      Math.abs(g.top - rect.top) < 5 && clampedLeft <= g.right + 8
    );
    if (match) {
      match.left   = Math.min(match.left,   clampedLeft);
      match.right  = Math.max(match.right,  clampedRight);
      match.top    = Math.min(match.top,    rect.top);
      match.bottom = Math.max(match.bottom, rect.bottom);
    } else {
      lineGroups.push({ top: rect.top, bottom: rect.bottom, left: clampedLeft, right: clampedRight });
    }
  });

  // Normalize all line groups in this paragraph to share the same left/right.
  // For the right edge: if we have a finite column-edge clamp from the gap
  // analysis, use that — it sits just inside the gutter and gives a clean
  // "full column width" look. Otherwise fall back to the widest line's right
  // (single-column pages where no gutter was detected).
  // For the left edge: use the leftmost line's left.
  // The result is a clean rectangular highlight per line instead of jagged
  // edges where short paragraph-final lines only covered their own text.
  if (lineGroups.length > 0) {
    const uniformLeft  = isFinite(clampLeft)
      ? clampLeft
      : Math.min(...lineGroups.map(g => g.left));
    const uniformRight = isFinite(clampRight)
      ? clampRight
      : Math.max(...lineGroups.map(g => g.right));
    lineGroups.forEach(g => { g.left = uniformLeft; g.right = uniformRight; });
  }

  const created = [];
  let first = true;
  lineGroups.forEach((b) => {
    const highlight = document.createElement('div');
    highlight.className = 'paragraph-highlight-word';
    highlight.style.position = 'absolute';
    highlight.style.left   = `${b.left  - containerRect.left - 1}px`;
    highlight.style.top    = `${b.top   - containerRect.top  - 1}px`;
    highlight.style.width  = `${b.right - b.left + 2}px`;
    highlight.style.height = `${b.bottom - b.top + 2}px`;
    highlight.style.backgroundColor = 'rgba(255, 220, 0, 0.28)';
    highlight.style.borderRadius = '1px';
    highlight.style.pointerEvents = 'none';
    highlight.style.zIndex = '1';
    if (first) {
      highlight.style.animation = 'highlightFadeIn 0.3s ease-out';
      first = false;
    }
    textLayerDiv.appendChild(highlight);
    created.push(highlight);
  });

  return created;
}

/**
 * Slice the portion of a span's text that lies inside the selection range,
 * snapping the cut points out to whole-word boundaries. A span holding a
 * selection endpoint is cut at its DOM offset; a span fully inside the range
 * returns its whole text. This is what gives single-word precision — pulling
 * "MapReduce" out of "…development of MapReduce, the authors…".
 */
function sliceSpanText(span, range) {
  const full = (span.textContent || '').replace(/ /g, ' ');
  if (!full) return '';
  let s = 0;
  let e = full.length;
  if (span === range.startContainer || span.contains(range.startContainer)) {
    s = Math.max(0, Math.min(range.startOffset, full.length));
  }
  if (span === range.endContainer || span.contains(range.endContainer)) {
    e = Math.max(0, Math.min(range.endOffset, full.length));
  }
  if (e < s) [s, e] = [e, s];
  while (s > 0 && /\w/.test(full[s - 1])) s--;
  while (e < full.length && /\w/.test(full[e])) e++;
  return full.slice(s, e);
}

/**
 * Reconstruct the selected text from spans already restricted to one column,
 * in visual reading order (top-to-bottom, left-to-right). Working from the live
 * spans + DOM offsets (rather than a textItems/textDivs index map) keeps it
 * precise and avoids the off-by-a-word bug that index mapping is prone to.
 */
function buildSelectionText(range, spans) {
  const parts = spans
    .map(span => {
      const r = span.getBoundingClientRect();
      return { x: r.left, y: r.top, right: r.right, text: sliceSpanText(span, range) };
    })
    .filter(p => p.text);
  if (parts.length === 0) return '';

  parts.sort((a, b) => (Math.abs(a.y - b.y) > 5 ? a.y - b.y : a.x - b.x));

  let result = parts[0].text;
  for (let k = 1; k < parts.length; k++) {
    const prev = parts[k - 1];
    const curr = parts[k];
    const sameLine = Math.abs(curr.y - prev.y) <= 5;
    const gap = curr.x - prev.right;
    // Decide spacing from the SLICED text we're actually appending — not the raw
    // span text. Mixing the two (raw for the whitespace test, sliced for the word
    // test) produced wrong spaces inside partial-word selections. sliceSpanText
    // preserves the span's own trailing/leading spaces, so \w$ / ^\w on the
    // sliced text already captures whether a join is mid-word.
    const prevEndsWord = /\w$/.test(prev.text);
    const currStartsWord = /^\w/.test(curr.text);
    const isWordContinuation = sameLine && gap < 2 && prevEndsWord && currStartsWord;
    result += isWordContinuation ? curr.text : ' ' + curr.text;
  }
  return result.normalize('NFKC').replace(/\s+/g, ' ').trim();
}

/**
 * Draw/update the blue selection highlight for one page's worth of selection
 * rects, clamped to the chosen column. One div is reused per visual text line
 * (nearest-top matching) across frames so each line smoothly tracks the drag.
 *
 * `rects`     — client rects pre-filtered to this page by the caller.
 * `container` — this page's textLayerDiv; new divs are appended here.
 * `pool`      — Set<{top, div}> per page. On mouseup the caller promotes these
 *               divs to the committed highlight by removing the preview class.
 */
// One box per visual line, clipped to the chosen column. Viewport coordinates.
function selectionLineBoxes(rects, clampLeft, clampRight) {
  const lineGroups = [];
  for (const rect of rects) {
    if (rect.width < 1 || rect.height < 1) continue;
    const left = Math.max(rect.left, clampLeft);
    const right = Math.min(rect.right, clampRight);
    if (right <= left) continue;
    const match = lineGroups.find(g => Math.abs(g.top - rect.top) < 5 && left <= g.right + 8);
    if (match) {
      match.left = Math.min(match.left, left);
      match.right = Math.max(match.right, right);
      match.top = Math.min(match.top, rect.top);
      match.bottom = Math.max(match.bottom, rect.bottom);
    } else {
      lineGroups.push({ top: rect.top, bottom: rect.bottom, left, right });
    }
  }
  return lineGroups;
}

function renderLivePreview(rects, container, clampLeft, clampRight, pool) {
  const containerRect = container.getBoundingClientRect();
  const lineGroups = selectionLineBoxes(rects, clampLeft, clampRight);

  const used = new Set();
  for (const b of lineGroups) {
    // Reuse the nearest unused pool div within a few px of this line's top.
    let entry = null;
    for (const e of pool) {
      if (used.has(e)) continue;
      if (Math.abs(e.top - b.top) <= 6) { entry = e; break; }
    }
    if (!entry) {
      const div = document.createElement('div');
      div.className = 'user-selection-highlight user-selection-preview';
      div.style.position = 'absolute';
      div.style.backgroundColor = 'rgba(59, 130, 246, 0.28)';
      div.style.borderBottom = '2px solid rgba(59, 130, 246, 0.7)';
      div.style.pointerEvents = 'none';
      div.style.zIndex = '2';
      container.appendChild(div);
      entry = { top: b.top, div };
      pool.add(entry);
    }
    entry.top = b.top; // track latest top for next frame's nearest-match
    const { div } = entry;
    div.style.left = `${b.left - containerRect.left}px`;
    div.style.top = `${b.top - containerRect.top}px`;
    div.style.width = `${b.right - b.left}px`;
    div.style.height = `${b.bottom - b.top}px`;
    used.add(entry);
  }

  // Drop divs for lines no longer in the selection.
  for (const e of [...pool]) {
    if (!used.has(e)) { e.div.remove(); pool.delete(e); }
  }
}

const PDFViewerCanvas = forwardRef(({
  pdfUrl,
  initialPage = 1,
  onVisiblePageChange,
  onSectionsExtracted,
  onTextSelected,
  paragraphLayout = [],
  annotations = [],
  onAnnotationClick,
}, ref) => {
  const pagesContainerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnMode, setColumnMode] = useState(1);
  const [userZoom, setUserZoom] = useState(1.0); // 1.0 = 100% (fit-to-width baseline)
  const [visiblePage, setVisiblePage] = useState(1);
  const [zoomModal, setZoomModal] = useState(null);
  const zoomCanvasRef = useRef(null);
  const zoomScrollRef = useRef(null);
  const [figureModal, setFigureModal] = useState(null);
  const figureCanvasRef = useRef(null);
  const visiblePageRef = useRef(null);
  const didInitialScrollRef = useRef(false);
  const pageDataRef = useRef(new Map());
  const selectionHighlightElemsRef = useRef([]);
  // Plan highlights (instructor cycle/test preview + the student's active-item
  // highlights) live in their own layer, tracked separately from the ephemeral
  // manual-selection highlight. They are PERSISTENT: never dismissed by a click
  // or scroll — only replaced when the highlight set changes (active item
  // advances, or the instructor edits/deletes a highlight).
  const planHighlightElemsRef = useRef([]);
  // Its own layer, so painting one does not wipe the plan highlights.
  const annotationHighlightElemsRef = useRef([]);
  // Map<pageNum, Set<{top,div}>> — one pool per page so divs are never
  // matched across page boundaries (each page has its own coordinate space).
  const livePreviewPoolRef = useRef(new Map());
  const onTextSelectedRef = useRef(onTextSelected);
  const layoutRef = useRef([]);
  const annotationsRef = useRef(annotations);
  const onAnnotationClickRef = useRef(onAnnotationClick);

  useEffect(() => {
    onTextSelectedRef.current = onTextSelected;
  }, [onTextSelected]);

  useEffect(() => {
    onAnnotationClickRef.current = onAnnotationClick;
  }, [onAnnotationClick]);

  // Drawn from server-resolved boxes, so they survive zoom and column changes.
  const paintAnnotationsOnPage = (pageNum) => {
    const pageData = pageDataRef.current.get(pageNum);
    if (!pageData) return;
    const { textLayerDiv, viewport } = pageData;
    textLayerDiv.querySelectorAll('.pb-annotation-marker').forEach(el => el.remove());
    const scale = viewport.scale;
    const MARKER = 20;
    const GAP = 6;
    // Positions already used, so markers on one passage do not stack.
    const taken = [];
    (annotationsRef.current || [])
      .filter(a => a.page === pageNum && Array.isArray(a.bbox) && a.bbox.length === 4)
      .forEach((a) => {
        const emphasis = a.kind === 'emphasis';
        const marker = document.createElement('button');
        marker.type = 'button';
        marker.className = `pb-annotation-marker${emphasis ? ' pb-annotation-marker--emphasis' : ''}`;
        marker.dataset.annotationId = String(a.id);
        marker.textContent = emphasis ? '!' : '?';
        marker.title = a.body || (emphasis ? 'Important passage' : '');
        // Vertically on the last highlighted line, horizontally out in the column's
        // margin: a selection ending mid-line would otherwise sit on the words after it.
        const lines = (Array.isArray(a.rects) && a.rects.length) ? a.rects : [a.bbox];
        const last = lines[lines.length - 1];
        const columnRight = Math.max(a.bbox[2], ...lines.map(r => r[2]));
        let left = columnRight * scale + GAP;
        const top = (last[1] + last[3]) / 2 * scale - MARKER / 2;
        while (taken.some(t => Math.abs(t.top - top) < MARKER && Math.abs(t.left - left) < MARKER + GAP)) {
          left += MARKER + GAP;
        }
        // Keep it on the page even when the passage ends at the right margin.
        const maxLeft = viewport.width - MARKER - 2;
        if (left > maxLeft) left = Math.max(2, maxLeft);
        taken.push({ left, top });
        marker.style.left = `${left}px`;
        marker.style.top = `${top}px`;
        marker.addEventListener('mousedown', (e) => e.stopPropagation());
        marker.addEventListener('click', (e) => {
          e.stopPropagation();
          // Where the marker sits, so the card can open beside it.
          if (onAnnotationClickRef.current) {
            onAnnotationClickRef.current(a, marker.getBoundingClientRect());
          }
        });
        textLayerDiv.appendChild(marker);
      });
  };

  const paintAllAnnotations = () => {
    // Across the container, not per page: pageDataRef is rebuilt on re-render.
    const container = pagesContainerRef.current;
    if (container) {
      container.querySelectorAll('.pb-annotation-marker').forEach(el => el.remove());
    }
    pageDataRef.current.forEach((_, pageNum) => paintAnnotationsOnPage(pageNum));
  };

  useEffect(() => {
    annotationsRef.current = annotations;
    paintAllAnnotations();
  }, [annotations]);

  // Dismiss a committed manual highlight on any mousedown. The per-page
  // textLayerDiv handler only fires when the click lands on a text span, so
  // clicks in the gutter / page background / figure / inter-span whitespace
  // used to leave the highlight up — which is why dismissing took several
  // clicks. A document-level listener catches those.
  //
  // Two things keep the highlight alive across a click and BOTH must be undone:
  //   1. Our committed overlay divs (tracked in selectionHighlightElemsRef).
  //   2. The native DOM Range. ::selection is rendered transparent, but a click
  //      on a non-text area never moves the caret, so the old range survives —
  //      and on a fresh page load that transparent range can still read back as
  //      a non-empty selection that re-arms downstream highlight logic.
  // We only collapse the range when a highlight was actually present, so an
  // unrelated in-progress selection elsewhere is never wiped. Not preventing
  // default means a brand-new drag-select still re-establishes its own range.
  // Capture phase so this runs BEFORE the per-page textLayerDiv mousedown
  // handler. That handler registers a mouseup that re-reads the selection and
  // re-commits it; by collapsing the range here first, that read comes back
  // empty and the highlight stays gone. Capture also means this still works if
  // hot-reload left a stale copy of the inner (bubble-phase) handler attached.
  useEffect(() => {
    const dismissOnMousedown = (e) => {
      if (selectionHighlightElemsRef.current.length === 0) return;
      // Preserve the select-then-ask flow: clicking into the chat (its context
      // chip + Define/Walkthrough/Note buttons live there) must keep the
      // highlight up. Only clicks elsewhere — the PDF, gutters, background —
      // dismiss it.
      if (e.target?.closest?.('.chat-section')) return;
      // Only the ephemeral manual-selection highlight is dismissed here. Plan
      // highlights (planHighlightElemsRef / .pb-plan-highlight) are persistent
      // and untouched, so neither a click nor a scroll removes them.
      selectionHighlightElemsRef.current.forEach(el => el.remove());
      selectionHighlightElemsRef.current = [];
      // Collapse the native range too. ::selection is transparent, but a click
      // on a non-text area never moves the caret, so the old range otherwise
      // survives and the per-page mouseup can resurrect the highlight from it.
      // Not calling preventDefault lets a brand-new drag-select rebuild its
      // own range normally.
      try { window.getSelection()?.removeAllRanges(); } catch {}
    };
    document.addEventListener('mousedown', dismissOnMousedown, true);
    return () => document.removeEventListener('mousedown', dismissOnMousedown, true);
  }, []);

  useEffect(() => {
    layoutRef.current = paragraphLayout;
  }, [paragraphLayout]);

  /**
   * Read the current window selection across ALL rendered pages and return the
   * per-page draw info needed to render preview/committed highlights.
   *
   * Works for both single-page and cross-page drags. Each page gets its own
   * column-clamp computed independently, so a two-column paper where the drag
   * starts at the bottom of page 1 and ends at the top of page 2 draws correct
   * highlights on both pages without leaking into the gutter.
   *
   * Returns null when there is no usable selection, otherwise:
   *   {
   *     selectedText: string,
   *     startPage: number,           // lowest page number touched
   *     pageGroups: Map<pageNum, {
   *       rects: DOMRect[],          // selection rects on this page
   *       textLayerDiv: Element,
   *       clampLeft: number,
   *       clampRight: number,
   *     }>
   *   }
   */
  const processSelection = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    if (range.collapsed) return null;

    const domText = selection.toString().replace(/\xa0/g, ' ').replace(/\s+/g, ' ').trim();
    if (!domText) return null;

    // Build rects per text span, but clip each span to the part the selection
    // actually covers. We can't use range.getClientRects() directly — on a
    // cross-page selection it returns full-page-height rects from PDF.js's
    // .endOfContent element and enclosed block containers (the "full page turns
    // blue" bug). But using each span's FULL bounding rect over-extends to the
    // whole word the moment the drag touches it (highlighting "privacy" before
    // you've dragged across it). The fix: for every span the range touches,
    // intersect a sub-range with that span's contents and measure THAT — giving
    // the exact partial-word extent, with no container artifacts.
    const pageGroups = new Map();
    for (const [pNum, pData] of pageDataRef.current) {
      // Cheap guard: skip pages the range doesn't touch at all.
      if (!range.intersectsNode(pData.textLayerDiv)) continue;
      const rects = [];
      for (const span of pData.textLayerDiv.querySelectorAll('span[role="presentation"]')) {
        if (!range.intersectsNode(span)) continue;
        // Clip a sub-range to the portion of THIS span that lies inside the
        // selection, so the preview tracks the exact characters dragged over.
        // Using span.getBoundingClientRect() instead would highlight all of
        // "What is privacy" when only "Wh" was selected. Confining the
        // sub-range to a single span also keeps getClientRects() free of the
        // endOfContent / block-container artifacts that pollute
        // range.getClientRects() on cross-page selections.
        const sub = document.createRange();
        sub.selectNodeContents(span);
        if (span === range.startContainer || span.contains(range.startContainer)) {
          try { sub.setStart(range.startContainer, range.startOffset); } catch {}
        }
        if (span === range.endContainer || span.contains(range.endContainer)) {
          try { sub.setEnd(range.endContainer, range.endOffset); } catch {}
        }
        for (const r of sub.getClientRects()) {
          if (r.width >= 1 && r.height >= 1) rects.push(r);
        }
      }
      if (rects.length === 0) continue;
      pageGroups.set(pNum, { rects, textLayerDiv: pData.textLayerDiv, viewport: pData.viewport });
    }
    if (pageGroups.size === 0) return null;

    const layout = layoutRef.current || [];

    // Compute per-page column clamp, reusing the same full-width / two-column
    // logic as before but applied independently to each page the selection covers.
    for (const [pNum, group] of pageGroups) {
      const { textLayerDiv, viewport, rects: pRects } = group;
      const containerRect = textLayerDiv.getBoundingClientRect();
      const scale = viewport.scale || 1;

      const pageParas = layout.filter(p => p.page === pNum);
      const leftParas  = pageParas.filter(p => p.column === 'left');
      const rightParas = pageParas.filter(p => p.column === 'right');
      const twoCol = leftParas.length > 0 && rightParas.length > 0;
      let splitX = containerRect.left + containerRect.width / 2;
      if (twoCol) {
        const lMax = Math.max(...leftParas.map(p => p.bbox[2]));
        const rMin = Math.min(...rightParas.map(p => p.bbox[0]));
        splitX = containerRect.left + ((lMax + rMin) / 2) * scale;
      }

      // Disable clamping for full-width blocks (abstract, title, etc.).
      let fullWidth = false;
      if (twoCol && pRects.length) {
        const midPdf = (((Math.min(...pRects.map(r => r.top)) + Math.max(...pRects.map(r => r.bottom))) / 2) - containerRect.top) / scale;
        const containing = pageParas.find(p => p.bbox[1] <= midPdf && p.bbox[3] >= midPdf);
        if (containing?.column === 'full') fullWidth = true;
      }
      const effectiveTwoCol = twoCol && !fullWidth;

      // Use the anchor element's column (for single-page selections) or the
      // first rect on this page (for subsequent pages in a cross-page drag).
      const anchorEl = selection.anchorNode
        ? (selection.anchorNode.nodeType === 1 ? selection.anchorNode : selection.anchorNode.parentElement)
        : null;
      const anchorSpan = anchorEl?.closest?.('span[role="presentation"]');
      let anchorLeft = true;
      if (anchorSpan && textLayerDiv.contains(anchorSpan)) {
        const ar = anchorSpan.getBoundingClientRect();
        anchorLeft = (ar.left + ar.right) / 2 < splitX;
      } else if (pRects.length) {
        anchorLeft = ((pRects[0].left + pRects[0].right) / 2) < splitX;
      }

      group.clampLeft  = (effectiveTwoCol && !anchorLeft) ? splitX : -Infinity;
      group.clampRight = (effectiveTwoCol &&  anchorLeft) ? splitX :  Infinity;
    }

    const startPage = Math.min(...pageGroups.keys());
    return { selectedText: domText, startPage, pageGroups };
  };

  // Load PDF document
  useEffect(() => {
    if (!pdfUrl) {
      setLoading(false);
      setPdfDoc(null);
      setNumPages(0);

      setError(
        'No PDF is configured for this paper. Upload a PDF in Django admin (Paper → PDF file).'
      );
      return undefined;
    }

    let cancelled = false;
    didInitialScrollRef.current = false;
    setLoading(true);
    setError(null);
    setPdfDoc(null);
    setNumPages(0);

  
    // load in any environment (dev falls back to localhost).
    const apiOrigin = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api').replace(/\/api\/?$/, '');
    const fullPdfUrl = pdfUrl.startsWith('http')
      ? pdfUrl
      : `${apiOrigin}${pdfUrl}`;

    const loadingTask = pdfjsLib.getDocument(fullPdfUrl);

    loadingTask.promise
      .then((pdf) => {
        if (cancelled) {
          pdf.destroy?.();
          return;
        }
        pdfDocRef.current = pdf;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setLoading(false);
        
        // Extract PDF outline/table of contents
        pdf.getOutline().then(outline => {
          if (outline && outline.length > 0) {
            console.log('PDF Outline:', JSON.stringify(outline, null, 2));
            parseOutline(outline, pdf).then(parsedSections => {
              console.log('Parsed Sections:', parsedSections);
              if (onSectionsExtracted) {
                onSectionsExtracted(parsedSections);
              }
            });
          } else {
            console.log('No outline found in PDF');
            if (onSectionsExtracted) {
              onSectionsExtracted([]);
            }
          }
        }).catch(err => {
          console.warn('Could not extract outline:', err);
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF');
        setLoading(false);
      });

    return () => {
      cancelled = true;
      loadingTask.destroy?.();
      const doc = pdfDocRef.current;
      pdfDocRef.current = null;
      doc?.destroy?.();
      setPdfDoc(null);
      setNumPages(0);

    };
  }, [pdfUrl]);

  const scrollToPage = (pageNum) => {
    const container = pagesContainerRef.current;
    if (!container) return false;
    const target = container.querySelector(`[data-page-number="${pageNum}"]`);
    if (!target) return false;
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
    return true;
  };

  const scrollToPageWithRetry = (pageNum, attempts = 10) => {
    if (!pageNum || pageNum < 1) return;
    let remaining = attempts;
    const tryScroll = () => {
      if (scrollToPage(pageNum)) return;
      remaining -= 1;
      if (remaining <= 0) return;
      window.requestAnimationFrame(tryScroll);
    };
    tryScroll();
  };

  /**
   * Draw highlight rects on a single rendered page. Rects are [x0,y0,x1,y1] in
   * PDF points (PyMuPDF top-left origin); css = pdf * viewport.scale, no y flip.
   * Appends to the page's text layer and tracks the divs for later clearing.
   * Returns the first created div (for scroll targeting), or null if the page
   * isn't rendered yet.
   */
  // Distinct shades let a student/instructor tell instructor-pinned highlights
  // (yellow) apart from AI-suggested ones (indigo) when both are shown at once.
  const HIGHLIGHT_COLORS = {
    instructor: 'rgba(255, 220, 0, 0.28)',
    ai: 'rgba(99, 102, 241, 0.24)',
    // Distinct from the cycle's pinned yellow when both are on screen.
    annotation: 'rgba(251, 146, 60, 0.22)',
  };

  const LAYER_CLASS = {
    selection: 'paragraph-highlight-word',
    plan: 'pb-plan-highlight',
    annotation: 'pb-annotation-highlight',
  };

  const drawRectsOnPage = (pageNum, rects, animateFirst, kind, persistent, layer) => {
    if (!rects || rects.length === 0) return null;
    const pageData = pageDataRef.current.get(pageNum);
    if (!pageData) return null;
    const { textLayerDiv, viewport } = pageData;
    const scale = viewport.scale;
    const color = HIGHLIGHT_COLORS[kind] || HIGHLIGHT_COLORS.instructor;
    // Persistent plan highlights get their own class + tracking ref so the
    // ephemeral click/scroll dismissal never touches them.
    const cls = LAYER_CLASS[layer] || (persistent ? 'pb-plan-highlight' : 'paragraph-highlight-word');
    const elemsRef = layer === 'annotation'
      ? annotationHighlightElemsRef
      : (persistent ? planHighlightElemsRef : selectionHighlightElemsRef);
    let firstDiv = null;
    rects.forEach((rect, idx) => {
      const [x0, y0, x1, y1] = rect;
      const div = document.createElement('div');
      div.className = cls;
      div.style.position = 'absolute';
      div.style.left   = `${x0 * scale - 1}px`;
      div.style.top    = `${y0 * scale - 1}px`;
      div.style.width  = `${(x1 - x0) * scale + 2}px`;
      div.style.height = `${(y1 - y0) * scale + 2}px`;
      div.style.backgroundColor = color;
      div.style.borderRadius = '1px';
      div.style.pointerEvents = 'none';
      div.style.zIndex = '1';
      if (idx === 0 && animateFirst) div.style.animation = 'highlightFadeIn 0.3s ease-out';
      textLayerDiv.appendChild(div);
      elemsRef.current.push(div);
      if (idx === 0) firstDiv = div;
    });
    return firstDiv;
  };

  /** Smooth-scroll so the first rect on `pageNum` sits near the top of the viewport. */
  const scrollToRect = (pageNum, rect) => {
    const pageData = pageDataRef.current.get(pageNum);
    const scrollContainer = pagesContainerRef.current;
    if (!pageData || !scrollContainer || !rect) return;
    const pageWrapper = pageData.textLayerDiv.closest('[data-page-number]');
    if (!pageWrapper) return;
    const containerRect = scrollContainer.getBoundingClientRect();
    const pageRect = pageWrapper.getBoundingClientRect();
    const relativePageTop = pageRect.top - containerRect.top + scrollContainer.scrollTop;
    const targetTop = relativePageTop + rect[1] * pageData.viewport.scale - 80;
    scrollContainer.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  };

  /**
   * Draw server-resolved highlights for a section that may span multiple pages.
   * groups — [{ page, rects: [[x0,y0,x1,y1], ...] }, ...] in PDF points.
   * Clears prior highlights, draws every group, then scrolls to the start.
   */
  const highlightLayoutGroups = (groups, persistent = false, scroll = true, layer = null) => {
    const valid = (groups || []).filter(g => g && g.rects && g.rects.length > 0);

    // Always clear prior paint of THIS layer — an empty group list is an
    // explicit "clear" (e.g. the instructor removed every highlight). Persistent
    // plan highlights clear only their own layer (.pb-plan-highlight); ephemeral
    // draws clear only theirs. Neither disturbs the other.
    const container = pagesContainerRef.current;
    const selector = layer === 'annotation'
      ? '.pb-annotation-highlight'
      : (persistent
          ? '.pb-plan-highlight'
          : '.paragraph-highlight-word, .user-selection-highlight');
    if (container) {
      container.querySelectorAll(selector).forEach(el => el.remove());
    }
    if (layer === 'annotation') annotationHighlightElemsRef.current = [];
    else if (persistent) planHighlightElemsRef.current = [];
    else selectionHighlightElemsRef.current = [];
    if (valid.length === 0) return false;

    const first = valid[0];

    // Where instructor and AI highlights cover the same text, the two
    // translucent layers would blend into a muddy brown. The instructor's shade
    // wins: drop AI rects substantially covered by an instructor rect on the
    // same page, so only the instructor's yellow shows on the shared text.
    const rectArea = (r) => Math.max(1, (r[2] - r[0]) * (r[3] - r[1]));
    const coveredFrac = (a, b) => {
      const ix = Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0]));
      const iy = Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
      return (ix * iy) / rectArea(a);
    };
    const instructorRectsByPage = new Map();
    valid.forEach(g => {
      if (g.kind === 'ai') return; // default/undefined kind counts as instructor
      instructorRectsByPage.set(g.page, [...(instructorRectsByPage.get(g.page) || []), ...g.rects]);
    });
    const drawGroups = valid
      .map(g => {
        if (g.kind !== 'ai') return g;
        const instr = instructorRectsByPage.get(g.page);
        if (!instr || !instr.length) return g;
        return { ...g, rects: g.rects.filter(r => !instr.some(ir => coveredFrac(r, ir) > 0.3)) };
      })
      .filter(g => g.rects.length > 0)
      // Draw AI first so the instructor's layer paints on top of any residual overlap.
      .sort((a, b) => (a.kind === 'ai' ? 0 : 1) - (b.kind === 'ai' ? 0 : 1));

    // scroll=false when the caller is already looking at the passage.
    if (scroll) scrollToPage(first.page);

    setTimeout(() => {
      let firstDiv = null;
      drawGroups.forEach((g, idx) => {
        const div = drawRectsOnPage(g.page, g.rects, idx === 0, g.kind, persistent, layer);
        if (idx === 0) firstDiv = div;
      });
      if (scroll && firstDiv) scrollToRect(first.page, first.rects[0]);
    }, scroll ? 300 : 0);

    return true;
  };

  useImperativeHandle(ref, () => ({
    scrollToPage,
    clearSelectionHighlights: () => {
      selectionHighlightElemsRef.current.forEach(el => el.remove());
      selectionHighlightElemsRef.current = [];
    },
    highlightParagraphOnPage: (pageNum, searchText) => {
      const normalizeText = (text) => text
        .normalize('NFKC')        // resolve ligatures: ﬁ→fi, ﬂ→fl
        .replace(/-\s+/g, '')     // rejoin line-break hyphens: "algo- rithm" → "algorithm"
        .toLowerCase()
        .replace(/[^\w\s]/g, ' ') // strip punctuation
        .replace(/\s+/g, ' ')     // collapse whitespace
        .trim();

      const normalizedSearch = normalizeText(searchText);

      const findOnPage = (pNum) => {
        const data = pageDataRef.current.get(pNum);
        if (!data) return null;
        const idx = data.paragraphs.findIndex(p => normalizeText(p.text).includes(normalizedSearch));
        return idx >= 0 ? { paragraphIndex: idx, pageData: data, resolvedPage: pNum } : null;
      };

      // Always clear highlights immediately — even if the new target can't be found,
      // the stale highlight from a previous query must not stay on screen.
      const container = pagesContainerRef.current;
      if (container) {
        container.querySelectorAll('.paragraph-highlight-word, .user-selection-highlight')
          .forEach(el => el.remove());
      }
      selectionHighlightElemsRef.current = [];

      let result = findOnPage(pageNum) || findOnPage(pageNum - 1) || findOnPage(pageNum + 1);

      if (!result) {
        console.warn(`"${searchText}" not found on pages ${pageNum - 1}–${pageNum + 1}, scanning all pages`);
        for (const pNum of pageDataRef.current.keys()) {
          result = findOnPage(pNum);
          if (result) break;
        }
        if (!result) {
          console.warn(`"${searchText}" not found anywhere — scrolling to page ${pageNum}`);
          scrollToPage(pageNum);
          return false;
        }
      }

      const { paragraphIndex, pageData, resolvedPage } = result;
      if (resolvedPage !== pageNum) {
        console.log(`Paragraph found on page ${resolvedPage} (requested ${pageNum})`);
      }
      console.log(`Highlight: found para[${paragraphIndex}] on p${resolvedPage}: "${pageData.paragraphs[paragraphIndex].text.slice(0, 60)}"`);

      scrollToPage(resolvedPage);

      setTimeout(() => {
        const paragraphs = pageData.paragraphs;
        const startPara = paragraphs[paragraphIndex];

        const isSectionHeading = (text) => {
          const t = text.trim();
          return t.length < 70 && /^\d+(\.\d+)*\s+[A-Z]/.test(t);
        };

        const toHighlight = [startPara];
        if (isSectionHeading(startPara.text)) {
          const MAX_SECTION_PARAGRAPHS = 15;
          for (
            let i = paragraphIndex + 1;
            i < paragraphs.length && toHighlight.length < MAX_SECTION_PARAGRAPHS;
            i++
          ) {
            if (isSectionHeading(paragraphs[i].text)) break;
            toHighlight.push(paragraphs[i]);
          }
        }

        console.log(`Highlighting ${toHighlight.length} paragraph(s) from index ${paragraphIndex}`);
        for (const para of toHighlight) {
          const anchor = (para.text || '').trim().slice(0, 30);
          const created = highlightParagraph(para, pageData.textLayerDiv, pageData.viewport, anchor);
          selectionHighlightElemsRef.current.push(...created);
        }
      }, 300);

      return true;
    },
    /**
     * Draw highlights from server-provided bounding boxes on a single page.
     * rects — [[x0,y0,x1,y1], ...] in PDF points, PyMuPDF top-left origin.
     */
    highlightFromLayout: (pageNum, rects, persistent = false) => {
      if (!rects || rects.length === 0) return false;
      return highlightLayoutGroups([{ page: pageNum, rects }], persistent);
    },

    /**
     * Draw highlights for a section that may span multiple pages.
     * groups — [{ page, rects: [[x0,y0,x1,y1], ...] }, ...] in PDF points.
     */
    highlightFromLayoutPages: (groups, persistent = false, scroll = true) =>
      highlightLayoutGroups(groups, persistent, scroll),
    paintAnnotationHighlight: (groups) =>
      highlightLayoutGroups(
        groups.map(g => ({ ...g, kind: 'annotation' })), true, false, 'annotation'),
    // Viewport bounds of the painted passage, so a card can open clear of it.
    getAnnotationHighlightBounds: () => {
      const rects = annotationHighlightElemsRef.current.map(el => el.getBoundingClientRect());
      if (!rects.length) return null;
      return {
        left: Math.min(...rects.map(r => r.left)),
        top: Math.min(...rects.map(r => r.top)),
        right: Math.max(...rects.map(r => r.right)),
        bottom: Math.max(...rects.map(r => r.bottom)),
      };
    },
    clearAnnotationHighlight: () => {
      const container = pagesContainerRef.current;
      if (container) {
        container.querySelectorAll('.pb-annotation-highlight').forEach(el => el.remove());
      }
      annotationHighlightElemsRef.current = [];
    },

    highlightParagraphByIndex: (pageNum, paragraphIndex) => {
      const pageData = pageDataRef.current.get(pageNum);

      if (!pageData) {
        console.warn(`Page ${pageNum} not found in pageData`);
        return false;
      }

      const paragraph = pageData.paragraphs[paragraphIndex];

      if (!paragraph) {
        console.warn(`Paragraph index ${paragraphIndex} not found on page ${pageNum}`);
        return false;
      }

      const container = pagesContainerRef.current;
      if (container) {
        container.querySelectorAll('.paragraph-highlight-word, .user-selection-highlight')
          .forEach(el => el.remove());
      }
      selectionHighlightElemsRef.current = [];

      scrollToPage(pageNum);

      setTimeout(() => {
        const anchor = (paragraph.text || '').trim().slice(0, 16);
        const created = highlightParagraph(paragraph, pageData.textLayerDiv, pageData.viewport, anchor);
        selectionHighlightElemsRef.current.push(...created);
      }, 300);

      return true;
    },
    zoomToFigure: (pageNum, figureHint, isFigure) => {
      if (!pdfDoc) return;
      let captionY = null;
      let resolvedPage = pageNum;

      if (figureHint && figureHint !== 'figure' && figureHint !== 'page') {
        const hint = String(figureHint).toLowerCase();
        const nearby = [pageNum, pageNum - 1, pageNum + 1];
        const rest = [...pageDataRef.current.keys()].filter(p => !nearby.includes(p));
        for (const p of [...nearby, ...rest]) {
          const pd = pageDataRef.current.get(p);
          if (!pd) continue;
          const found = pd.paragraphs.find(para => para.text.toLowerCase().includes(hint));
          if (found) { captionY = found.startY; resolvedPage = p; break; }
        }
      }

      if (isFigure) {
        let figureTopPdfY = null;
        if (captionY != null) {
          const pd = pageDataRef.current.get(resolvedPage);
          if (pd) {
            const above = pd.paragraphs
              .filter(para => para.startY > captionY + 5)
              .sort((a, b) => a.startY - b.startY);
            if (above.length === 1) {
              figureTopPdfY = above[0].startY;
            } else if (above.length > 1) {
              let maxGap = 0, splitIdx = above.length - 1;
              for (let i = 0; i < above.length - 1; i++) {
                const gap = above[i + 1].startY - above[i].startY;
                if (gap > maxGap) { maxGap = gap; splitIdx = i + 1; }
              }
              figureTopPdfY = maxGap > 25 ? above[splitIdx].startY : above[above.length - 1].startY;
            }
          }
        }
        setFigureModal({ pageNum: resolvedPage, captionY, figureTopPdfY, figureHint });
      } else {
        scrollToPage(resolvedPage);
        setZoomModal({ pageNum: resolvedPage, captionY });
      }
    },
  }));

  // Re-render the target page at 3× scale inside the zoom modal
  useEffect(() => {
    if (!zoomModal || !pdfDoc) return;
    let cancelled = false;
    const render = async () => {
      const canvas = zoomCanvasRef.current;
      const scroller = zoomScrollRef.current;
      if (!canvas || !scroller) return;
      const page = await pdfDoc.getPage(zoomModal.pageNum);
      const SCALE = 3;
      const vp = page.getViewport({ scale: SCALE });
      canvas.width = vp.width;
      canvas.height = vp.height;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      if (cancelled) return;
      if (zoomModal.captionY != null) {
        const [, capVpY] = vp.convertToViewportPoint(0, zoomModal.captionY);
        scroller.scrollTop = Math.max(0, capVpY - scroller.clientHeight * 0.75);
      }
    };
    render().catch(console.error);
    return () => { cancelled = true; };
  }, [zoomModal, pdfDoc]);

  // Close zoom modal on Escape
  useEffect(() => {
    if (!zoomModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomModal(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomModal]);

  // Render cropped figure into the figure modal canvas
  useEffect(() => {
    if (!figureModal || !pdfDoc) return;
    // Bail if pageNum isn't a valid integer in range — avoids pdfDoc.getPage(undefined)
    // which throws and leaves the canvas blank (the "black popup" bug).
    const pNum = Number(figureModal.pageNum);
    if (!Number.isInteger(pNum) || pNum < 1 || pNum > (pdfDoc.numPages || 0)) {
      setFigureModal(null);
      return;
    }
    let cancelled = false;
    const render = async () => {
      const canvas = figureCanvasRef.current;
      if (!canvas) return;
      const page = await pdfDoc.getPage(pNum);
      const SCALE = 2.5;
      const vp = page.getViewport({ scale: SCALE });

      const offscreen = document.createElement('canvas');
      offscreen.width = vp.width;
      offscreen.height = vp.height;
      await page.render({ canvasContext: offscreen.getContext('2d'), viewport: vp }).promise;
      if (cancelled) return;

      let captionVpY = vp.height * 0.65;
      if (figureModal.captionY != null) {
        [, captionVpY] = vp.convertToViewportPoint(0, figureModal.captionY);
      }

      let cropTop = 0;
      if (figureModal.figureTopPdfY != null) {
        const [, topVpY] = vp.convertToViewportPoint(0, figureModal.figureTopPdfY);
        cropTop = Math.max(0, topVpY - 10);
      } else {
        cropTop = Math.max(0, captionVpY - vp.height * 0.45);
      }

      const cropBottom = Math.min(vp.height, captionVpY + 80);
      const cropHeight = Math.max(1, cropBottom - cropTop);

      canvas.width = vp.width;
      canvas.height = cropHeight;
      canvas.getContext('2d').drawImage(offscreen, 0, cropTop, vp.width, cropHeight, 0, 0, vp.width, cropHeight);
    };
    render().catch(console.error);
    return () => { cancelled = true; };
  }, [figureModal, pdfDoc]);

  // Close figure modal on Escape
  useEffect(() => {
    if (!figureModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setFigureModal(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [figureModal]);

  // Render all pages; re-render on container resize via internal ResizeObserver
  useEffect(() => {
    if (!pdfDoc || !pagesContainerRef.current || numPages < 1) {
      return undefined;
    }

    const container = pagesContainerRef.current;
    let cancelled = false;
    let renderGen = 0;

    const renderPages = async () => {
      const myGen = ++renderGen;
      const containerW = container.clientWidth;
      if (containerW < 10 || cancelled) return;

      container.classList.toggle('two-column', columnMode === 2);

      // On re-renders (e.g. resize/zoom), scroll back to the page the user was on.
      // Capture visiblePageRef BEFORE clearing so zoom doesn't flash to page 1.
      const scrollTarget = didInitialScrollRef.current
        ? (visiblePageRef.current || initialPage)
        : initialPage;

      // Hide container during re-render to avoid flash to page 1
      container.style.visibility = 'hidden';
      container.innerHTML = '';
      pageDataRef.current.clear();
      selectionHighlightElemsRef.current = [];

      // Per-page available width: full container minus padding (40),
      // halved when in 2-column mode (no inter-page gap).
      const perPageWidth = columnMode === 2
        ? (containerW - 40) / 2
        : containerW - 40;

      const outputScale = Math.max(window.devicePixelRatio || 1, 2);

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        if (cancelled || renderGen !== myGen) return;

        const pageWrapper = document.createElement('div');
        pageWrapper.className = 'page-wrapper unlocked';
        pageWrapper.setAttribute('data-page-number', String(pageNum));
        // Width is set explicitly after viewport is known (see below)

        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-canvas';
        pageWrapper.appendChild(canvas);

        try {
          const page = await pdfDoc.getPage(pageNum);
          if (cancelled || renderGen !== myGen) return;
          const naturalVp = page.getViewport({ scale: 1 });
          const baseScale = Math.min(2.5, Math.max(0.5, perPageWidth / naturalVp.width));
          const scale = baseScale * userZoom;
          const viewport = page.getViewport({ scale });

          // Set page wrapper to exact canvas width so flex layout knows the real size
          pageWrapper.style.width = `${viewport.width}px`;

          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;

          const context = canvas.getContext('2d');
          await page.render({
            canvasContext: context,
            viewport,
            transform: outputScale !== 1
              ? [outputScale, 0, 0, outputScale, 0, 0]
              : null,
          }).promise;

          // Create text layer for selection
          const textLayerDiv = document.createElement('div');
          textLayerDiv.className = 'textLayer';
          textLayerDiv.style.width = `${viewport.width}px`;
          textLayerDiv.style.height = `${viewport.height}px`;
          // PDF.js 3.x writes each span's left/top/fontSize as
          // `calc(var(--scale-factor)*…px)`. Without this variable the calc is
          // invalid and spans get default font sizes — which misaligns text
          // selection (a click on one word maps to a neighbouring word) and
          // throws off column geometry. Must equal the viewport scale.
          textLayerDiv.style.setProperty('--scale-factor', `${scale}`);
          pageWrapper.appendChild(textLayerDiv);

          const textContent = await page.getTextContent();
          const textDivs = [];

          await pdfjsLib.renderTextLayer({
            textContentSource: textContent,
            container: textLayerDiv,
            viewport: viewport,
            textDivs: textDivs,
          }).promise;

          // Detect paragraphs for this page (pass page width in PDF points so
          // the column-jump thresholds adapt to non-letter page sizes).
          const paragraphs = detectParagraphs(textContent, naturalVp.width);
          console.log(`Page ${pageNum} - Detected ${paragraphs.length} paragraphs`);

          // Store paragraphs and viewport for external highlighting.
          // scale and naturalWidth are needed by highlightFromLayout to convert
          // server PDF-point coordinates → CSS pixel positions.
          pageDataRef.current.set(pageNum, {
            paragraphs: paragraphs,
            viewport: {
              width: viewport.width,
              height: viewport.height,
              scale: scale,
              naturalWidth: naturalVp.width,  // page width in PDF points (≈ PyMuPDF page.rect.width)
            },
            textLayerDiv: textLayerDiv,
            textItems: textContent.items.map(it => it.str),  // clean PDF strings, no \xa0 injection
            textDivs: textDivs,                              // 1:1 with textItems
          });

          paintAnnotationsOnPage(pageNum);

          // Store paragraphs as data attribute
          textLayerDiv.setAttribute('data-paragraphs', JSON.stringify(paragraphs));
          textLayerDiv.setAttribute('data-viewport', JSON.stringify({
            width: viewport.width,
            height: viewport.height
          }));

          // Render clickable hyperlink annotations
          try {
            const annotations = await page.getAnnotations({ intent: 'display' });
            const linkAnnotations = annotations.filter(a => a.subtype === 'Link');
            if (linkAnnotations.length > 0) {
              const annotLayerDiv = document.createElement('div');
              annotLayerDiv.className = 'pdf-annot-layer';
              annotLayerDiv.style.position = 'absolute';
              annotLayerDiv.style.top = '0';
              annotLayerDiv.style.left = '0';
              annotLayerDiv.style.width = `${viewport.width}px`;
              annotLayerDiv.style.height = `${viewport.height}px`;
              annotLayerDiv.style.pointerEvents = 'none';
              pageWrapper.appendChild(annotLayerDiv);

              for (const annot of linkAnnotations) {
                const [x1, y1, x2, y2] = annot.rect;
                const [vx1, vy1] = viewport.convertToViewportPoint(x1, y1);
                const [vx2, vy2] = viewport.convertToViewportPoint(x2, y2);
                const left = Math.min(vx1, vx2);
                const top = Math.min(vy1, vy2);
                const width = Math.abs(vx2 - vx1);
                const height = Math.abs(vy2 - vy1);

                const linkEl = document.createElement('a');
                linkEl.style.cssText = `position:absolute;left:${left}px;top:${top}px;width:${width}px;height:${height}px;cursor:pointer;pointer-events:all;z-index:4;`;

                // PDF.js may put the URL in several places depending on version
                const url = annot.url || annot.unsafeUrl || annot.action?.URI || null;

                if (url) {
                  linkEl.href = url;
                  linkEl.target = '_blank';
                  linkEl.rel = 'noopener noreferrer';
                } else if (annot.dest) {
                  // Internal page link — scroll to destination page
                  linkEl.href = '#';
                  const dest = annot.dest;
                  linkEl.addEventListener('click', async (e) => {
                    e.preventDefault();
                    try {
                      const resolved = typeof dest === 'string'
                        ? await pdfDocRef.current.getDestination(dest)
                        : dest;
                      if (resolved) {
                        const pageIdx = await pdfDocRef.current.getPageIndex(resolved[0]);
                        const targetPage = pagesContainerRef.current?.querySelector(`[data-page-number="${pageIdx + 1}"]`);
                        targetPage?.scrollIntoView({ behavior: 'smooth' });
                      }
                    } catch {}
                  });
                } else {
                  continue; // no usable target, skip
                }
                annotLayerDiv.appendChild(linkEl);
              }
            }
          } catch {}

          // Live selection preview + cross-page drag support.
          // mousedown fires per-page but onMove/onUp are document-level and
          // use processSelection() which works across all pages, so dragging
          // from the bottom of one page to the top of the next just works.
          textLayerDiv.addEventListener('mousedown', (downEvent) => {
            const startX = downEvent.clientX;
            const startY = downEvent.clientY;

            // Clear any committed highlight across all pages.
            selectionHighlightElemsRef.current.forEach(el => el.remove());
            selectionHighlightElemsRef.current = [];

            // Clear all per-page preview pools.
            const pools = livePreviewPoolRef.current;
            pools.forEach(pagePool => { pagePool.forEach(e => e.div.remove()); });
            pools.clear();

            // Helper: get-or-create the pool Set for a given page.
            const pagePool = (pNum) => {
              if (!pools.has(pNum)) pools.set(pNum, new Set());
              return pools.get(pNum);
            };

            // Helper: remove all preview divs for pages no longer in the selection.
            const pruneStalePages = (activePageNums) => {
              for (const [pNum, pPool] of [...pools]) {
                if (!activePageNums.has(pNum)) {
                  pPool.forEach(e => e.div.remove());
                  pools.delete(pNum);
                }
              }
            };

            let rafPending = false;
            let moved = false;
            // Treat the gesture as a drag only once the pointer leaves a small
            // dead-zone. A plain click commonly emits a stray sub-pixel
            // mousemove, so a bare "any movement" flag would still classify
            // clicks as drags and re-commit the stale selection.
            const DRAG_THRESHOLD = 4;
            const onMove = (e) => {
              if (!moved) {
                if (Math.abs(e.clientX - startX) < DRAG_THRESHOLD &&
                    Math.abs(e.clientY - startY) < DRAG_THRESHOLD) return;
                moved = true;
              }
              if (rafPending) return;
              rafPending = true;
              window.requestAnimationFrame(() => {
                rafPending = false;
                const res = processSelection();
                if (res) {
                  pruneStalePages(new Set(res.pageGroups.keys()));
                  for (const [pNum, group] of res.pageGroups) {
                    renderLivePreview(group.rects, group.textLayerDiv, group.clampLeft, group.clampRight, pagePool(pNum));
                  }
                } else {
                  pools.forEach(pPool => { pPool.forEach(e => e.div.remove()); });
                  pools.clear();
                }
              });
            };

            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              // A plain click (no drag) must not re-commit a highlight. The
              // previous selection's native range stays alive (it's rendered
              // transparent) and a click often doesn't collapse it on the
              // PDF.js text layer, so processSelection would return that stale
              // range and re-draw the highlight we just cleared on mousedown —
              // which is why dismissing used to take several clicks. With no
              // drag, just drop any preview and leave the highlight cleared.
              // A double- or triple-click selects a word or line with no drag at
              // all, which is how most people pick out a single term. Only treat
              // a no-drag release as a dismissal when nothing is actually selected.
              const clickSelection = (() => {
                try {
                  const sel = window.getSelection();
                  return sel && !sel.isCollapsed && sel.toString().trim() ? sel : null;
                } catch { return null; }
              })();
              if (!moved && !clickSelection) {
                pools.forEach(pPool => { pPool.forEach(e => e.div.remove()); });
                pools.clear();
                // Collapse the lingering (transparent) native range. Without
                // this the old selection stays alive and a later click — or a
                // jittery one that just trips the threshold — feeds it back
                // into processSelection and re-draws the cleared highlight.
                try { window.getSelection()?.removeAllRanges(); } catch {}
                return;
              }
              setTimeout(() => {
                const res = processSelection();
                if (res) {
                  // Final frame — then promote all preview divs to committed.
                  pruneStalePages(new Set(res.pageGroups.keys()));
                  for (const [pNum, group] of res.pageGroups) {
                    renderLivePreview(group.rects, group.textLayerDiv, group.clampLeft, group.clampRight, pagePool(pNum));
                  }
                  pools.forEach(pPool => {
                    pPool.forEach(e => {
                      e.div.classList.remove('user-selection-preview');
                      selectionHighlightElemsRef.current.push(e.div);
                    });
                  });
                  pools.clear();
                  // The same line boxes the preview drew, in PDF points.
                  const group = res.pageGroups.get(res.startPage);
                  let pdfRects = [];
                  if (group) {
                    const box = group.textLayerDiv.getBoundingClientRect();
                    const sc = group.viewport.scale || 1;
                    pdfRects = selectionLineBoxes(group.rects, group.clampLeft, group.clampRight)
                      .map(b => [
                        (b.left - box.left) / sc,
                        (b.top - box.top) / sc,
                        (b.right - box.left) / sc,
                        (b.bottom - box.top) / sc,
                      ]);
                  }
                  onTextSelectedRef.current?.(res.selectedText, res.startPage, pdfRects);
                } else {
                  pools.forEach(pPool => { pPool.forEach(e => e.div.remove()); });
                  pools.clear();
                }
              }, 50);
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          });

          // Make URL text clickable — collect all span texts, find URLs, open on click
          textLayerDiv.addEventListener('click', (e) => {
            const allSpans = Array.from(textLayerDiv.querySelectorAll('span'));
            const clickedIdx = allSpans.indexOf(e.target);
            if (clickedIdx === -1) return;

            const start = Math.max(0, clickedIdx - 5);
            const end = Math.min(allSpans.length - 1, clickedIdx + 5);
            const nearby = allSpans.slice(start, end + 1).map(s => s.textContent || '').join('');

            const urlMatch = nearby.match(/https?:\/\/[^\s,)\]"'<>]+/i);
            if (urlMatch) {
              e.preventDefault();
              e.stopPropagation();
              window.open(urlMatch[0].replace(/[.,;:]+$/, ''), '_blank', 'noopener,noreferrer');
            }
          });

          // Show indicator for pages with no selectable text
          const itemCount = textContent.items ? textContent.items.length : 0;
          if (itemCount === 0) {
            const noText = document.createElement('div');
            noText.className = 'no-text-indicator';
            noText.textContent = 'No selectable text on this page (image-only PDF).';
            pageWrapper.appendChild(noText);
          }
        } catch (err) {
          console.error(`Error rendering page ${pageNum}:`, err);
        }

        // Page number label
        const pageLabel = document.createElement('div');
        pageLabel.className = 'page-label';
        pageLabel.textContent = `Page ${pageNum}`;
        pageWrapper.appendChild(pageLabel);

        if (cancelled || renderGen !== myGen) return;

        if (columnMode === 2) {
          // In two-column mode, group pages into rows of 2 so each row can scroll horizontally
          const lastRow = container.lastElementChild;
          if (!lastRow || lastRow.classList.contains('pdf-page-row--full')) {
            const row = document.createElement('div');
            row.className = 'pdf-page-row';
            row.appendChild(pageWrapper);
            container.appendChild(row);
          } else {
            lastRow.appendChild(pageWrapper);
            lastRow.classList.add('pdf-page-row--full');
          }
        } else {
          container.appendChild(pageWrapper);
        }
      }

      if (!cancelled && renderGen === myGen) {
        container.style.minWidth = '';
        didInitialScrollRef.current = true;
        scrollToPageWithRetry(scrollTarget, 20);
        container.style.visibility = '';
      }
    };

    renderPages();

    // Observe the parent panel for width changes (not the pages container itself,
    // which changes height as pages are added and would cause an infinite loop)
    const observeTarget = container.parentElement ?? container;
    let lastObservedWidth = observeTarget.clientWidth;
    let debounce;
    const observer = new ResizeObserver(() => {
      const newWidth = observeTarget.clientWidth;
      if (newWidth === lastObservedWidth) return;
      lastObservedWidth = newWidth;
      clearTimeout(debounce);
      debounce = setTimeout(() => { if (!cancelled) renderPages(); }, 150);
    });
    observer.observe(observeTarget);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
      observer.disconnect();
      container.innerHTML = '';
    };
  }, [pdfDoc, numPages, initialPage, columnMode, userZoom]);

  // Reset scroll flag when initialPage changes
  useEffect(() => {
    didInitialScrollRef.current = false;
  }, [initialPage]);

  // Track visible page
  useEffect(() => {
    const container = pagesContainerRef.current;
    if (!container || !onVisiblePageChange || numPages < 1) return undefined;

    let raf = null;
    const handler = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        const wrappers = container.querySelectorAll('[data-page-number]');
        if (!wrappers.length) return;

        let bestPage = null;
        let bestTop = Infinity;
        const containerRect = container.getBoundingClientRect();

        wrappers.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const intersects = rect.bottom > containerRect.top && rect.top < containerRect.bottom;
          if (!intersects) return;
          const topDistance = Math.abs(rect.top - containerRect.top);
          if (topDistance < bestTop) {
            bestTop = topDistance;
            bestPage = parseInt(el.getAttribute('data-page-number'), 10);
          }
        });

        if (bestPage && visiblePageRef.current !== bestPage) {
          visiblePageRef.current = bestPage;
          onVisiblePageChange(bestPage);
        }
      });
    };

    container.addEventListener('scroll', handler, { passive: true });
    handler();

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      container.removeEventListener('scroll', handler);
    };
  }, [onVisiblePageChange, numPages]);

  if (loading) {
    return (
      <div className="pdf-viewer-loading">
        <div className="spinner"></div>
        <p>Loading PDF...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-viewer-error">
        <p>{error}</p>
        <p>PDF URL: {pdfUrl}</p>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-viewer-header">
        <span className="pdf-page-label">
          Page {visiblePageRef.current}{numPages ? ` of ${numPages}` : ''}
        </span>
        <div className="zoom-controls" role="group" aria-label="Zoom">
          {userZoom !== 1.0 && (
            <button
              type="button"
              className="zoom-ctrl-btn zoom-reset-btn"
              onClick={() => setUserZoom(1.0)}
              title="Reset zoom to 100%"
            >Reset</button>
          )}
          <button
            type="button"
            className="zoom-ctrl-btn"
            onClick={() => setUserZoom(z => Math.max(1.0, parseFloat((z - 0.1).toFixed(1))))}
            disabled={userZoom <= 1.0}
            title="Zoom out"
          >−</button>
          <span className="zoom-level">{Math.round(userZoom * 100)}%</span>
          <button
            type="button"
            className="zoom-ctrl-btn"
            onClick={() => setUserZoom(z => Math.min(3.0, parseFloat((z + 0.1).toFixed(1))))}
            disabled={userZoom >= 3.0}
            title="Zoom in"
          >+</button>
        </div>
        <div className="column-toggle" role="group" aria-label="Page layout">
          <button
            type="button"
            className={`column-toggle-btn ${columnMode === 1 ? 'active' : ''}`}
            onClick={() => setColumnMode(1)}
            aria-pressed={columnMode === 1}
            title="Single page per row"
          >
            1 column
          </button>
          <button
            type="button"
            className={`column-toggle-btn ${columnMode === 2 ? 'active' : ''}`}
            onClick={() => setColumnMode(2)}
            aria-pressed={columnMode === 2}
            title="Two pages side-by-side"
          >
            2 columns
          </button>
        </div>
      </div>

      <div
        ref={pagesContainerRef}
        className="pdf-pages-container"
      />

      {zoomModal && (
        <div className="zoom-overlay" onClick={() => setZoomModal(null)}>
          <div className="zoom-modal" onClick={e => e.stopPropagation()}>
            <button className="zoom-close-btn" onClick={() => setZoomModal(null)}>×</button>
            <div className="zoom-scroll" ref={zoomScrollRef}>
              <canvas ref={zoomCanvasRef} className="zoom-canvas" />
            </div>
          </div>
        </div>
      )}

      {figureModal && (
        <div className="figure-overlay" onClick={() => setFigureModal(null)}>
          <div className="figure-modal" onClick={e => e.stopPropagation()}>
            <div className="figure-modal-header">
              <span className="figure-modal-title">{figureModal.figureHint || 'Figure'}</span>
              <button className="figure-modal-close" onClick={() => setFigureModal(null)}>×</button>
            </div>
            <div className="figure-modal-body">
              <canvas ref={figureCanvasRef} className="figure-modal-canvas" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PDFViewerCanvas;